import type { RootState } from "./index";

const LANGUAGE_CHANGE_STATE_KEY = "rbc-language-change-state";

/**
 * Keys to exclude from state persistence.
 * RTK Query state and other API-related state should not be persisted
 * as they contain active subscriptions and non-serializable data.
 */
const EXCLUDED_KEYS = [
  // RTK Query API slices - these contain active query subscriptions
  "experimentsApi",
  "hotelsPostBooking",
  "carsPostBooking",
  "activitiesPostBooking",
  "packagesPostBookingApi",
  // Activities state - contains locale-specific data (activity names, descriptions)
  "activities",
  // Legacy CFAR keys that may not exist in current reducer
  "hotelsCfarExercise",
  "hotelsCfarCancelQuote",
  "flightsCfarExercise",
  "flightsCfarCancelQuote",
  "hotelCfarOffers",
];

/**
 * Save Redux state to sessionStorage before language change reload.
 * This preserves user's session data (search results, checkout data, etc.)
 * across the reload to prevent unwanted redirects.
 *
 * Excludes RTK Query and API state to avoid rehydration errors.
 */
export function saveStateForLanguageChange(state: RootState): void {
  try {
    // Filter out RTK Query and other problematic state
    const filteredState: Partial<RootState> = {};
    for (const [key, value] of Object.entries(state)) {
      if (!EXCLUDED_KEYS.includes(key)) {
        filteredState[key as keyof RootState] = value;
      }
    }

    const serialized = JSON.stringify(filteredState);
    const sizeInMB = (serialized.length / (1024 * 1024)).toFixed(2);
    sessionStorage.setItem(LANGUAGE_CHANGE_STATE_KEY, serialized);
    console.log(
      `[LanguagePersistence] Saved filtered Redux state (${sizeInMB}MB) before language change`
    );
  } catch (error) {
    console.error("[LanguagePersistence] Failed to save state:", error);
    // Continue with reload even if save fails
  }
}

/**
 * Restore Redux state from sessionStorage after language change reload.
 * Returns the saved state if available, otherwise returns undefined.
 */
export function loadStateAfterLanguageChange(): Partial<RootState> | undefined {
  try {
    const serialized = sessionStorage.getItem(LANGUAGE_CHANGE_STATE_KEY);
    if (!serialized) {
      console.log("[LanguagePersistence] No saved state found");
      return undefined;
    }

    console.log(
      `[LanguagePersistence] Found saved state, attempting to restore...`
    );

    // Clear the saved state immediately after reading
    sessionStorage.removeItem(LANGUAGE_CHANGE_STATE_KEY);

    const state = JSON.parse(serialized);
    console.log(
      "[LanguagePersistence] Successfully restored Redux state after language change",
      { keys: Object.keys(state) }
    );
    return state;
  } catch (error) {
    console.error("[LanguagePersistence] Failed to load state:", error);
    // Clear corrupted data
    sessionStorage.removeItem(LANGUAGE_CHANGE_STATE_KEY);
    // Don't block app startup - just log the error and continue without state
    console.warn("[LanguagePersistence] Continuing without restored state");
    return undefined;
  }
}

/**
 * Check if we're in a language change reload scenario
 */
export function isLanguageChangeReload(): boolean {
  return sessionStorage.getItem(LANGUAGE_CHANGE_STATE_KEY) !== null;
}
